/**
 * Event in a Box — Slice 1 wizard ("one-step composition").
 *
 * Guided flow over the shipped provisioning primitives. See
 * docs/DESIGN_EVENT_IN_A_BOX.md §3/§4/E3 and ITERATION 4 (workflow modes).
 *
 * Steps: workflow mode → award (+guest) → mode-specific → audience →
 * invitation → preview/launch. Nothing is written until LAUNCH (draft-then-
 * launch, §4); the invitation is scheduled LAST (E3) so a mid-launch failure
 * cannot have emailed anyone.
 */

import { confirmPromptReachLine } from "../../services/eibAudienceReach";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../ui/Toast";
import {
  launchEventInABoxAuto,
  getLatestEibLaunchForRehydrate,
  chooseEibLaunchKey,
  findResumableEibLaunch,
  EIB_RESUMABLE_STEPS,
  summarizePlan,
  describeBlueprint,
  describeTypeCadence,
  cadenceOffsetLabel,
  estimateAudience,
  errorText,
  EIB_AUDIENCE_REVIEW_THRESHOLD,
  type EibPlan,
  type EibWorkflowMode,
  type EibAwardKind,
  type EibAudience,
  type EibContactType,
  type EibLaunchResult,
  type EibAudienceEstimate,
  type EibResumeCandidate,
  type EibStepKey,
} from "../../services/eventInABox.service";
import { listEibAudienceLists } from "../../services/eventLandingPage.service";
import {
  getEventOrganizations,
  getEventById,
} from "../../services/events.service";
import { provisionWalkInOnly } from "../../services/eventTicketing.service";
import type { UUID } from "../../types/common.types";
import { defaultInvitationSubject } from "../../utils/eventVenueName";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onLaunched: () => void;
  event: {
    id: UUID;
    title: string;
    start_date?: string | null;
    /** Event type — drives the fixed invitation cadence (gun_show, range_night). */
    type?: string | null;
  } | null;
  /** Pre-selected workflow when opened from the create hand-off: 'show_up' for a
   *  conversion/no-registration event, 'pre_register' when registration is required.
   *  Prevents provisioning a registration workflow for a walk-up event. */
  initialMode?: EibWorkflowMode;
  /** Embedded mode: render inline as part of the unified create wizard (no modal
   *  overlay, no own header/step-rail) instead of a standalone full-screen modal.
   *  The parent supplies the page chrome + the continuous step rail. */
  embedded?: boolean;
  /** In embedded mode, reports the current step's 0-based index within STEP_ORDER
   *  (and the total) so the parent can highlight the unified rail. */
  onStepChange?: (index: number, total: number) => void;
  /** Standalone (non-embedded) only: when provided, the post-launch success footer
   *  offers an "Add CTA / QR" action. Unlike the embedded create flow — which has a
   *  built-in CTA step — the event-page modal has none, so without this the operator
   *  is left at a dead end after launch. The parent should close the modal and open
   *  the event's Call to Action tab. */
  onAddCta?: () => void;
}

type Step = "mode" | "award" | "modeDetail" | "audience" | "invite" | "preview";

const STEP_ORDER: Step[] = [
  "mode",
  "award",
  "modeDetail",
  "audience",
  "invite",
  "preview",
];

// Walk-in only reuses just the first three steps. It never sends an invitation, so it skips
// audience/invite/preview and finishes via the atomic eib_provision_walk_in_only RPC (provisions
// ticketing AND enables check-in AND sets the walk-in flags — see migration 030). It does NOT go
// through launchEventInABox and is NOT a server workflow_mode (the launch RPCs + workflow_mode
// CHECK only know 'pre_register'/'show_up'); the walk-in distinction lives on the event's flags.
const WALK_IN_ONLY_STEP_ORDER: Step[] = ["mode", "award", "modeDetail"];

type WorkflowChoice = EibWorkflowMode | "walk_in_only";

export default function EventInABoxWizard({
  isOpen,
  onClose,
  onLaunched,
  event,
  initialMode,
  embedded = false,
  onStepChange,
  onAddCta,
}: Props) {
  const { user } = useAuth();
  const { showToast } = useToast();

  const [step, setStep] = useState<Step>("mode");
  const [mode, setMode] = useState<WorkflowChoice>(
    initialMode ?? "pre_register",
  );

  // Award. rewardEnabled=true → "Ticket + automatic upgrade reward" (default);
  // false → "Ticket only" (a $0 admission ticket, no upgrade reward; staff upgrade
  // manually on the event page). Ticket-only maps to award kind 'none' at launch.
  const [rewardEnabled, setRewardEnabled] = useState(true);
  const [awardKind, setAwardKind] = useState<EibAwardKind>("voucher");
  const [awardName, setAwardName] = useState("");
  const [awardValue, setAwardValue] = useState<string>("");
  // O1: guest award is per-event OPT-IN, never a mode default.
  const [guestAwardOn, setGuestAwardOn] = useState(false);
  const [guestAwardKind, setGuestAwardKind] = useState<EibAwardKind>("voucher");
  const [guestAwardName, setGuestAwardName] = useState("");
  const [guestAwardValue, setGuestAwardValue] = useState<string>("");

  // Mode detail
  const [registrationMode, setRegistrationMode] = useState<
    "invite_gated" | "open"
  >("invite_gated");
  const [openCap, setOpenCap] = useState<string>("");
  const [checkinAck, setCheckinAck] = useState(false);
  // Show-up events have no pre-registration signal, so capacity is a useful planning
  // estimate + optional self-check-in issuance cap. OPTIONAL (tester feedback 2026-08-15):
  // self check-ins are themselves counted attendees, so this is not the only signal. Blank
  // ⇒ null in buildPlan ⇒ uncapped.
  //
  // COLLIDES WITH the create wizard's "Max Capacity" box (both are asked in one create
  // pass). Verified against the live PROD function bodies 2026-09-09: this value reaches
  // `eib_apply_workflow_settings` / `eib_provision_walk_in_only`, which set
  // events.capacity and events.walk_in_limit outright and
  // events.max_attendees = GREATEST(COALESCE(max_attendees,0), capacity) — and when it is
  // NULL, set ALL THREE to NULL. So leaving this blank DISCARDS a max_attendees the
  // operator typed at create, while leaving events.attendance_hard_cap (which nothing
  // here writes) switched on with nothing left to enforce. Latent, not burning: all 14
  // PROD hard-cap events are pre_register range nights, and the pre_register branch
  // returns before this UPDATE. Do not "simplify" either box away — see the note at the
  // create wizard's Max Capacity field.
  const [expectedCapacity, setExpectedCapacity] = useState<string>("");

  // Audience
  const [audienceKind, setAudienceKind] = useState<"existing" | "proximity">(
    "existing",
  );
  const [existingListId, setExistingListId] = useState<string>("");
  const [audienceLists, setAudienceLists] = useState<
    Array<{
      id: string;
      name: string;
      contact_count: number | null;
      is_dynamic: boolean;
    }>
  >([]);
  // Proximity audience = OR-union of the selected atomic contact types. Required
  // multi-select (at least one); defaults to current members. Replaces the former
  // single-preset membership radio (members/active/active+prospects/everyone).
  const [contactTypes, setContactTypes] = useState<EibContactType[]>([
    "current",
  ]);
  const [radiusMiles, setRadiusMiles] = useState<string>("25");
  const [anchorType, setAnchorType] = useState<"organization" | "zip">(
    "organization",
  );
  const [anchorZip, setAnchorZip] = useState("");
  const [eventOrgId, setEventOrgId] = useState<string | null>(null);
  // Whether THIS event carries its own geocoded coordinates. Proximity anchors on the event's own
  // lat/lng (within_radius_of_event) when it does — this works even when the event's org/location
  // is un-geocoded, which is exactly the case that used to break proximity targeting.
  const [eventIsGeocoded, setEventIsGeocoded] = useState(false);

  // Invitation — the DESIGN is resolved server-side from (mode, guest award);
  // it is deliberately not operator-typed (code-audit HIGH / Seam C4).
  const [flightSubject, setFlightSubject] = useState("");
  const [flightSendAt, setFlightSendAt] = useState("");
  // Slice 2 (reminders): days-before-event to send an invitation reminder
  // (each ∈ {0,1,2}; 0 = day-of, 08:00 event-local). Default day-of on; empty =
  // no reminders. Persisted to event_in_a_box_launches.reminder_offsets.
  const [reminderOffsets, setReminderOffsets] = useState<number[]>([0]);

  // Preview / launch
  const [audienceCount, setAudienceCount] =
    useState<EibAudienceEstimate | null>(null);
  const [launching, setLaunching] = useState(false);
  const [launchProgress, setLaunchProgress] = useState<string | null>(null);
  const [launchResult, setLaunchResult] = useState<EibLaunchResult | null>(
    null,
  );
  // §5.g — a launch that exceeds the REVIEW threshold stops (before anything is
  // scheduled) and asks the operator to TYPE the exact audience size. A tick box
  // is too easy to click through for a one-way mass send.
  const [confirmPrompt, setConfirmPrompt] = useState<string | null>(null);
  const [typedCount, setTypedCount] = useState<string>("");
  // Set when the server refuses because this event was already launched; the
  // operator must explicitly ask for an ADDITIONAL invitation wave.
  const [additionalWavePrompt, setAdditionalWavePrompt] = useState<
    string | null
  >(null);
  const [allowAdditionalWave, setAllowAdditionalWave] = useState(false);
  // ONE key per launch attempt. A transport retry reuses it (so the server
  // reconciles instead of sending twice); an intentional new wave mints a new one.
  const [idempotencyKey, setIdempotencyKey] = useState<string>(() =>
    crypto.randomUUID(),
  );
  const [error, setError] = useState<string | null>(null);
  // Resume (2026-10-16): when this event's latest attempt failed after its audience was frozen and
  // the plan is unchanged, Launch continues THAT attempt (its key, campaign, invitation and frozen
  // audience) instead of creating another draft campaign. The operator can opt out.
  const [resumeCandidate, setResumeCandidate] = useState<Extract<
    EibResumeCandidate,
    { resumable: true }
  > | null>(null);
  const [resumeOptOut, setResumeOptOut] = useState(false);
  const resumeGenRef = useRef(0);
  // FIX 3 — rehydration outcome. Blank defaults are only a SAFE launch state when
  // we have conclusively established there is no prior plan (not_found) or fully
  // loaded one (ok). While 'loading', or on 'error'/'unusable', Launch is
  // disabled so we never send with a blank config that silently stood in for the
  // operator's real prior setup.
  const [rehydrateStatus, setRehydrateStatus] = useState<
    "idle" | "loading" | "ok" | "not_found" | "unusable" | "error"
  >("idle");
  // FIX 2 — request fence tied to the wizard-open generation. Every open bumps
  // this ref; an in-flight rehydrate captures the value at fire time and its
  // response is applied ONLY while the ref still matches. Opening event B before
  // event A's fetch resolves therefore cannot let A's plan overwrite B's state.
  const rehydrateGenRef = useRef(0);
  // Which event the subject default was seeded for; a late event fetch for a previous open is ignored.
  const subjectEventRef = useRef<UUID | null>(null);

  const eventId = event?.id ?? null;
  const eventTitle = event?.title ?? "";
  // Fixed invitation cadence for this event type (gun_show, range_night): when
  // set, the SERVER owns the invite send time + reminder offsets, so the wizard
  // shows the cadence read-only instead of the manual send-time/reminder controls.
  const typeCadence = describeTypeCadence(event?.type);

  // Walk-in only drops audience/invite/preview (client-only, no invitation) so the wizard
  // ends on the Check-in step with a provision-only "Create event".
  const stepOrder =
    mode === "walk_in_only" ? WALK_IN_ONLY_STEP_ORDER : STEP_ORDER;
  const isLastStep = stepOrder.indexOf(step) === stepOrder.length - 1;
  // A wave-B hard-ceiling partial is a PERMANENT decision — an unchanged retry
  // cannot pass the ceiling, so the launch-result surface omits the retry offer.
  const partialCeiling =
    launchResult?.partialSuccess === true &&
    (launchResult.partialWaveB?.detail?.includes("EIB_AUDIENCE_HARD_CEILING") ??
      false);
  // Launch is blocked while a prior-plan lookup is unresolved ('loading') or came
  // back inconclusive ('error'/'unusable') — sending then risks a blank config
  // that silently replaced the operator's real prior setup (FIX 3).
  const rehydrateBlocksLaunch =
    rehydrateStatus === "loading" ||
    rehydrateStatus === "error" ||
    rehydrateStatus === "unusable";

  // FIX 2/FIX 3 — fetch the prior launch plan and apply it UNDER THE REQUEST
  // FENCE. Factored out of the open-effect so the Launch-step "Try again" control
  // can re-run just the lookup after a transient error without re-seeding blanks.
  const loadRehydration = (targetEventId: UUID) => {
    // Capture this open's generation; a newer open bumps the ref and any earlier
    // in-flight response (even for another event) is then ignored below.
    const myGen = ++rehydrateGenRef.current;
    setRehydrateStatus("loading");
    getLatestEibLaunchForRehydrate(targetEventId)
      .then((res) => {
        if (rehydrateGenRef.current !== myGen) {
          return; // stale — a newer wizard-open has superseded this fetch
        }
        if (res.status === "error") {
          setRehydrateStatus("error");
          return;
        }
        if (res.status === "unusable") {
          // A launch row exists but in an unrecognized schema — refuse to guess
          // its fields, and do NOT present the event as a blank launchable draft.
          setRehydrateStatus("unusable");
          return;
        }
        if (res.status === "not_found") {
          setRehydrateStatus("not_found"); // legitimate new config — stay blank
          return;
        }
        // status === "ok": override the blanked setters ONLY for fields the stored
        // plan actually carries (null ⇒ keep the blank default), so a partial plan
        // never clears a field.
        const r = res.data;
        if (r.mode) {
          setMode(r.mode);
        }
        if (r.rewardEnabled !== null) {
          setRewardEnabled(r.rewardEnabled);
        }
        if (r.awardKind) {
          setAwardKind(r.awardKind);
        }
        if (r.awardName !== null) {
          setAwardName(r.awardName);
        }
        if (r.awardValue !== null) {
          setAwardValue(r.awardValue);
        }
        if (r.guestAwardOn) {
          setGuestAwardOn(true);
          if (r.guestAwardKind) {
            setGuestAwardKind(r.guestAwardKind);
          }
          if (r.guestAwardName !== null) {
            setGuestAwardName(r.guestAwardName);
          }
          if (r.guestAwardValue !== null) {
            setGuestAwardValue(r.guestAwardValue);
          }
        }
        if (r.registrationMode) {
          setRegistrationMode(r.registrationMode);
        }
        if (r.openCap !== null) {
          setOpenCap(r.openCap);
        }
        if (r.expectedCapacity !== null) {
          setExpectedCapacity(r.expectedCapacity);
        }
        if (r.checkinAck !== null) {
          setCheckinAck(r.checkinAck);
        }
        if (r.audienceKind) {
          setAudienceKind(r.audienceKind);
        }
        if (r.existingListId !== null) {
          setExistingListId(r.existingListId);
        }
        if (r.contactTypes && r.contactTypes.length > 0) {
          setContactTypes(r.contactTypes);
        }
        if (r.radiusMiles !== null) {
          setRadiusMiles(r.radiusMiles);
        }
        if (r.anchorType) {
          setAnchorType(r.anchorType);
        }
        if (r.anchorZip !== null) {
          setAnchorZip(r.anchorZip);
        }
        if (r.flightSubject) {
          setFlightSubject(r.flightSubject);
        }
        if (r.flightSendAt !== null) {
          setFlightSendAt(r.flightSendAt);
        }
        if (r.reminderOffsets) {
          setReminderOffsets(r.reminderOffsets);
        }
        setRehydrateStatus("ok");
      })
      .catch(() => {
        if (rehydrateGenRef.current !== myGen) {
          return;
        }
        // A thrown/rejected lookup is an ERROR, not "no previous launch": block
        // Launch rather than silently proceeding with blank defaults.
        setRehydrateStatus("error");
      });
  };

  useEffect(() => {
    if (!isOpen || !eventId) {
      return;
    }
    // Code-audit MED: opening the wizard on a DIFFERENT event must not inherit
    // the previous event's award/audience/capacity — every field is re-seeded.
    setStep("mode");
    setMode(initialMode ?? "pre_register");
    setAwardKind("voucher");
    setAwardName("");
    setAwardValue("");
    setGuestAwardOn(false);
    setGuestAwardKind("voucher");
    setGuestAwardName("");
    setGuestAwardValue("");
    setRegistrationMode("invite_gated");
    setOpenCap("");
    setCheckinAck(false);
    setExpectedCapacity("");
    setAudienceKind("existing");
    setExistingListId("");
    setContactTypes(["current"]);
    setRadiusMiles("25");
    setAnchorType("organization");
    setAnchorZip("");
    setFlightSendAt("");
    setAudienceCount(null);
    setLaunchResult(null);
    setError(null);
    setConfirmPrompt(null);
    setTypedCount("");
    setAdditionalWavePrompt(null);
    setAllowAdditionalWave(false);
    setIdempotencyKey(crypto.randomUUID());
    resumeGenRef.current++;
    setResumeCandidate(null);
    setResumeOptOut(false);
    // Title-based default until the event row (with type + Location Name) arrives below.
    const titleDefaultSubject = defaultInvitationSubject({
      title: eventTitle,
      type: event?.type,
    });
    setFlightSubject(titleDefaultSubject);
    subjectEventRef.current = eventId;
    listEibAudienceLists()
      .then(setAudienceLists)
      .catch(() => setAudienceLists([]));
    getEventOrganizations(eventId)
      .then((orgs) => {
        const first = (orgs as Array<{ organization_id?: string }> | null)?.[0];
        setEventOrgId(first?.organization_id ?? null);
      })
      .catch(() => setEventOrgId(null));
    // Determine whether the event itself is geocoded, so proximity can anchor on the event's own
    // coordinates (within_radius_of_event) rather than the org — which is often un-geocoded even
    // when the event/venue is (the original proximity-targeting bug).
    getEventById(eventId)
      .then((ev) => {
        const e = ev as {
          latitude?: number | null;
          longitude?: number | null;
          location_name?: string | null;
        } | null;
        // Range Night subjects name the venue from Location Name, so they read the same whether or
        // not the title follows the naming SOP. Replace ONLY the untouched default: a subject the
        // operator already typed, or one restored from a saved launch plan, is kept.
        if (subjectEventRef.current === eventId) {
          const venueDefault = defaultInvitationSubject({
            title: eventTitle,
            type: event?.type,
            location_name: e?.location_name,
          });
          setFlightSubject((prev) =>
            prev === titleDefaultSubject ? venueDefault : prev,
          );
        }
        setEventIsGeocoded(
          typeof e?.latitude === "number" && typeof e?.longitude === "number",
        );
      })
      .catch(() => setEventIsGeocoded(false));
    // FIX 3 — rehydrate from a prior LAUNCH ATTEMPT. The blank re-seed above is
    // the correct default for a brand-new configuration; but if this event was
    // already configured up to a launch (committed, in-flight, or a FAILED
    // attempt), that plan is frozen in event_in_a_box_launches.plan, and
    // reopening the wizard should restore it rather than start empty. We override
    // the blanked setters ONLY for fields the stored plan actually carries
    // (null ⇒ keep the blank default), so a partial plan never clears a field.
    // LIMITATION: an event saved BEFORE any launch attempt persists NO
    // EIB-specific data (buildPlan lives only in local component state until
    // Launch), so a pure never-launched draft has nothing to rehydrate — that
    // would require separate per-step draft persistence, deliberately out of
    // scope here. eventOrgId is intentionally left to getEventOrganizations above
    // (the proximity anchor org == the event's org) to avoid racing that fetch.
    loadRehydration(eventId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, eventId, initialMode]);

  // Report the active step to the parent so the unified rail (embedded mode) can
  // highlight it as one continuous wizard.
  useEffect(() => {
    if (isOpen) {
      onStepChange?.(stepOrder.indexOf(step), stepOrder.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, isOpen, onStepChange, mode]);

  // Resume preview: on the review step, ask the server whether Launch will continue an earlier
  // attempt, so the operator sees it BEFORE clicking. doLaunch asks again (authoritatively).
  useEffect(() => {
    if (
      !isOpen ||
      !event ||
      step !== "preview" ||
      launchResult ||
      mode === "walk_in_only" ||
      resumeOptOut ||
      (rehydrateStatus !== "ok" && rehydrateStatus !== "not_found")
    ) {
      return;
    }
    const myGen = ++resumeGenRef.current;
    findResumableEibLaunch(buildPlan()).then((r) => {
      if (resumeGenRef.current !== myGen) {
        return;
      }
      setResumeCandidate(r.resumable ? r : null);
    });
    // buildPlan reads the wizard's input state, which cannot change while the review step is shown.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    event?.id,
    step,
    launchResult,
    mode,
    resumeOptOut,
    rehydrateStatus,
    allowAdditionalWave,
  ]);

  if (!isOpen || !event) {
    return null;
  }

  const reset = () => {
    setStep("mode");
    setLaunchResult(null);
    setError(null);
    setCheckinAck(false);
    setLaunching(false);
    setConfirmPrompt(null);
    setTypedCount("");
    setAdditionalWavePrompt(null);
    setAllowAdditionalWave(false);
    setIdempotencyKey(crypto.randomUUID());
    resumeGenRef.current++;
    setResumeCandidate(null);
    setResumeOptOut(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const buildAudience = (): EibAudience =>
    audienceKind === "existing"
      ? { kind: "existing", listId: existingListId as UUID }
      : {
          kind: "proximity",
          // The multi-select ALWAYS emits the "contact_types" scope (never
          // "members"), so launchEventInABoxAuto falls through to the single
          // current-member cadence wave and never triggers the #8 dual-wave split.
          scope: "contact_types",
          contactTypes,
          radiusMiles: Number(radiusMiles) || 25,
          anchor:
            anchorType === "organization"
              ? // "Event venue" anchor: prefer the EVENT's own geocoded coordinates
                // (within_radius_of_event), which are present even when the event's
                // organization is not geocoded; fall back to the org anchor otherwise.
                eventIsGeocoded
                ? { type: "event", eventId: eventId as UUID }
                : {
                    type: "organization",
                    organizationId: (eventOrgId ?? "") as UUID,
                  }
              : { type: "zip", zip: anchorZip.trim() },
        };

  const buildPlan = (): EibPlan => ({
    eventId: event.id,
    eventTitle: event.title,
    eventStartAt: event.start_date ?? new Date().toISOString(),
    eventType: event.type ?? null,
    // walk_in_only never reaches buildPlan (it finishes via doCreateWalkInOnly, not
    // launchEventInABox), so the plan's server mode is always pre_register/show_up here.
    mode: mode as EibWorkflowMode,
    // Ticket-only ⇒ award kind 'none', no member/guest award; the $0 admission
    // ticket is reused as-is and does not auto-escalate.
    awardKind: rewardEnabled ? awardKind : "none",
    awardName: rewardEnabled ? awardName.trim() : null,
    awardValue: rewardEnabled
      ? awardValue === ""
        ? null
        : Number(awardValue)
      : null,
    guestAward:
      rewardEnabled && guestAwardOn
        ? {
            kind: guestAwardKind,
            name: guestAwardName.trim(),
            value: guestAwardValue === "" ? null : Number(guestAwardValue),
          }
        : null,
    registrationMode: mode === "pre_register" ? registrationMode : undefined,
    openIssuanceCap:
      mode === "pre_register" && registrationMode === "open" && openCap !== ""
        ? Number(openCap)
        : null,
    audience: buildAudience(),
    flightSubject: flightSubject.trim(),
    flightSendAt: flightSendAt ? new Date(flightSendAt).toISOString() : "",
    reminderOffsets: [...reminderOffsets].sort((a, b) => a - b),
    idempotencyKey,
    expectedCapacity:
      mode === "show_up" && expectedCapacity !== ""
        ? Number(expectedCapacity)
        : null,
    checkinAcknowledged: mode === "show_up" ? checkinAck : false,
    typedAudienceCount: typedCount === "" ? null : Number(typedCount),
    allowAdditionalWave,
    userId: (user?.id ?? "") as UUID,
  });

  /** Per-step gate — the wizard refuses to advance on incomplete input so the
   *  Launch step never assembles a half-formed plan. */
  const stepError = (s: Step): string | null => {
    if (s === "award") {
      // Ticket-only: nothing to validate — no reward is configured.
      if (!rewardEnabled) {
        return null;
      }
      if (!awardName.trim()) {
        return "Give the award a name — it appears on the member's reward.";
      }
      if (awardKind === "voucher" && awardValue === "") {
        return "A voucher award needs a dollar value.";
      }
      if (guestAwardOn && !guestAwardName.trim()) {
        return "Name the guest award, or turn the guest award off.";
      }
      if (
        guestAwardOn &&
        guestAwardKind === "voucher" &&
        guestAwardValue === ""
      ) {
        return "The guest voucher award needs a dollar value.";
      }
    }
    if (s === "modeDetail") {
      if (mode === "show_up") {
        // Expected attendance is OPTIONAL (tester feedback 2026-08-15). A show-up event DOES
        // produce registrations — each self check-in is recorded as an attendee and counted —
        // so this is a planning estimate + optional issuance cap, not the "only" signal. Blank
        // ⇒ null in buildPlan ⇒ uncapped issuance.
        if (!checkinAck) {
          return "Confirm that public self check-in will be opened for this event.";
        }
      }
      // Open registration has no invite list to bound issuance, so the server
      // (set_event_registration_mode → open_mode_requires_positive_cap) REQUIRES a
      // positive cap. Enforce it here so the operator fixes it in the wizard instead of
      // hitting the guard at launch. (invite_gated needs no cap — the invite list bounds it.)
      if (mode === "pre_register" && registrationMode === "open") {
        const cap = Number(openCap);
        if (openCap.trim() === "" || !Number.isInteger(cap) || cap <= 0) {
          return "Open registration needs a positive ticket cap — the maximum number of registrations to allow. With no invite list, the cap is the only thing limiting how many tickets are issued.";
        }
      }
    }
    if (s === "audience") {
      if (audienceKind === "existing" && !existingListId) {
        return "Choose the segment to invite.";
      }
      if (audienceKind === "proximity") {
        // Required multi-select: at least one contact type must be chosen. An
        // empty selection would fail closed in the builder, but we block here so
        // the operator gets an explicit hint instead of a silent narrowing.
        if (contactTypes.length === 0) {
          return "Pick at least one contact type to invite.";
        }
        if (anchorType === "organization" && !eventIsGeocoded && !eventOrgId) {
          return "This event isn't geocoded and has no organization to anchor the radius to — use a ZIP instead.";
        }
        if (anchorType === "zip" && !/^\d{5}$/.test(anchorZip.trim())) {
          return "Enter a 5-digit ZIP for the radius anchor.";
        }
      }
    }
    if (s === "invite") {
      if (!flightSubject.trim()) {
        return "The invitation needs a subject line.";
      }
      // Fixed-cadence types schedule automatically — the operator does not pick a
      // send time, so there is nothing to validate here.
      if (!typeCadence) {
        if (!flightSendAt) {
          return "Choose when the invitation should send.";
        }
        if (new Date(flightSendAt).getTime() < Date.now()) {
          return "The invitation send time is in the past.";
        }
      }
    }
    return null;
  };

  const goNext = async () => {
    const err = stepError(step);
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    const i = stepOrder.indexOf(step);
    const next = stepOrder[i + 1];
    if (next === "preview") {
      const count = await estimateAudience(buildAudience());
      setAudienceCount(count);
    }
    setStep(next);
  };

  const goBack = () => {
    setError(null);
    const i = stepOrder.indexOf(step);
    if (i > 0) {
      setStep(stepOrder[i - 1]);
    }
  };

  const doLaunch = async () => {
    setLaunching(true);
    setError(null);
    try {
      // #8: launchEventInABoxAuto splits a proximity "members" audience into a
      // current-member wave + a former/cancelled win-back wave automatically;
      // every other audience is a single wave (unchanged).
      // Resume: continue this event's earlier failed attempt when the server says it is safe (same
      // plan, same operator, untouched drafts). The chosen key is kept in state, so an in-session
      // retry ("Back to review" -> Launch) uses the same key, exactly as before.
      const chosen = await chooseEibLaunchKey(buildPlan(), { resumeOptOut });
      if (chosen.plan.idempotencyKey !== idempotencyKey) {
        setIdempotencyKey(chosen.plan.idempotencyKey);
      }
      setResumeCandidate(chosen.resumed?.resumable ? chosen.resumed : null);
      const res = await launchEventInABoxAuto(chosen.plan, setLaunchProgress);

      // #8 auto-dual-wave PARTIAL (wave A committed, wave B did not) MUST be
      // handled BEFORE the generic server-decision interception below — those
      // paths say "Nothing has been sent", which is FALSE once wave A scheduled.
      if (res.partialSuccess) {
        const wbDetail = res.partialWaveB?.detail ?? "";
        if (wbDetail.includes("EIB_AUDIENCE_CONFIRM_REQUIRED")) {
          // Wave A is scheduled; the FORMER/cancelled audience is above the
          // review threshold. Confirm for wave B ONLY — typing its size and
          // launching again re-runs with the SAME key, so wave A reconciles
          // idempotently (current members are NOT re-invited) and only the
          // win-back wave proceeds.
          setConfirmPrompt(
            `Current-member invitations are already scheduled. The former/cancelled win-back audience is ${
              res.partialWaveB?.audienceCount?.toLocaleString() ??
              "a large number of"
            } people, above the ${EIB_AUDIENCE_REVIEW_THRESHOLD.toLocaleString()} review threshold. Type that exact size to confirm and send the win-back wave — current members will NOT be invited again.`,
          );
          setLaunching(false);
          setLaunchProgress(null);
          return;
        }
        // Any other wave-B failure: wave A really did schedule, so show the
        // honest partial surface — NEVER the "nothing sent" red panel. A hard
        // ceiling is PERMANENT (an unchanged retry can't pass it), so it renders
        // without a retry offer (see the launch-result block); everything else
        // offers the same-key "Retry win-back wave".
        setLaunchResult(res);
        showToast(
          "warning",
          wbDetail.includes("EIB_AUDIENCE_HARD_CEILING")
            ? "Current-member invitations are scheduled. The former/cancelled win-back audience exceeds the maximum, so that wave was not sent."
            : "Current-member invitations are scheduled, but the former/cancelled win-back wave didn't finish. Use “Retry win-back wave” — current members won't be invited again.",
        );
        onLaunched();
        setLaunching(false);
        setLaunchProgress(null);
        return;
      }

      // Below here NOTHING has committed (single wave, or a pre-commit stop): the
      // server-side audience gate and already-launched guard are decisions, not
      // errors — turn each into its specific confirmation. Nothing was emailed.
      const stopped = res.ok
        ? null
        : (res.steps.find((x) => x.status === "failed")?.detail ?? "");
      if (stopped && stopped.includes("EIB_AUDIENCE_HARD_CEILING")) {
        // Deliberately NOT bypassable from the wizard.
        setError(stopped);
        setLaunching(false);
        setLaunchProgress(null);
        return;
      }
      if (stopped && stopped.includes("EIB_AUDIENCE_CONFIRM_REQUIRED")) {
        // ⚠️ The old wording said the invitation "would go to N people". It does not: measured on
        // PROD 2026-09-24, 22.6%-42.9% of a frozen EIB audience never becomes a recipient, mostly
        // because the snapshot gate drops addresses NeverBounce has never evaluated. Telling an
        // operator the frozen number at the moment they type a confirmation was the whole reason
        // the shortfall stayed invisible. The typed number must still be the FROZEN count, because
        // that is what eib_authorize_audience compares against.
        const frozen = res.audienceCount ?? null;
        // NOTE: today this is always "" -- eib_authorize_audience raises CONFIRM_REQUIRED before
        // getEibAudienceQuality runs, so res.audienceQuality is unset here. Kept (with honest wording)
        // for when the preview moves ahead of authorisation.
        const reachLine = confirmPromptReachLine(frozen, res.audienceQuality);
        setConfirmPrompt(
          `This invitation is addressed to ${
            frozen?.toLocaleString() ?? "a large number of"
          } people, above the ${EIB_AUDIENCE_REVIEW_THRESHOLD.toLocaleString()} review threshold. Nothing has been sent.${reachLine} Type the exact audience size (${
            frozen?.toLocaleString() ?? "the number above"
          }) below to confirm.`,
        );
        setLaunching(false);
        setLaunchProgress(null);
        return;
      }
      if (stopped && stopped.includes("EIB_ALREADY_LAUNCHED")) {
        setAdditionalWavePrompt(stopped);
        setLaunching(false);
        setLaunchProgress(null);
        return;
      }

      setLaunchResult(res);
      if (res.ok) {
        const cadenceAdjusted = res.steps.some(
          (step) => step.status === "warning",
        );
        showToast(
          cadenceAdjusted ? "warning" : "success",
          cadenceAdjusted
            ? `${event.title} is set up. Its normal invitation window had passed, so the first invitation was scheduled for the next 8:00 AM in the event's timezone.`
            : `${event.title} is set up and the invitation is scheduled.`,
        );
        onLaunched();
      } else {
        showToast(
          "error",
          `Launch stopped at "${res.failedAt}". No invitation was sent — fix the issue and launch again.`,
        );
      }
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLaunching(false);
      setLaunchProgress(null);
    }
  };

  // Walk-in only: no invitation to launch. One authorized, atomic server op provisions ticketing,
  // ENABLES public self check-in (raw provision_event_ticketing DISABLES it — re-audit BLOCKER-1),
  // sets the walk-in flags, and persists the optional capacity (null ⇒ uncapped). No launch/flight.
  const doCreateWalkInOnly = async () => {
    setLaunching(true);
    setError(null);
    try {
      const res = await provisionWalkInOnly({
        eventId: event.id,
        awardKind: rewardEnabled ? awardKind : "none",
        awardName: rewardEnabled ? awardName.trim() : null,
        awardValue:
          rewardEnabled && awardValue !== "" ? Number(awardValue) : null,
        guestAwardKind: rewardEnabled && guestAwardOn ? guestAwardKind : null,
        guestAwardName:
          rewardEnabled && guestAwardOn ? guestAwardName.trim() : null,
        guestAwardValue:
          rewardEnabled && guestAwardOn && guestAwardValue !== ""
            ? Number(guestAwardValue)
            : null,
        expectedCapacity:
          expectedCapacity !== "" ? Number(expectedCapacity) : null,
      });
      // Runtime-validate the FULL success shape (re-audit BLOCKER): a partial or blocked RPC
      // response (e.g. provisioning refused with tickets_already_issued, or a malformed cast) must
      // never be reported to the operator as a ready walk-in event. Every field must be exactly right.
      if (
        res.provisioned !== true ||
        res.checkin_enabled !== true ||
        res.self_checkin_disabled !== false ||
        res.allow_walk_ins !== true ||
        res.registration_required !== false
      ) {
        throw new Error(
          "Walk-in setup did not fully complete — public self check-in did not enable, or provisioning was refused. Do not open doors on this event; contact support.",
        );
      }
      setLaunchResult({
        ok: true,
        steps: [
          {
            key: "ticketing",
            label: "Ticket and award set up — walk-in self check-in is ON",
            status: "ok",
          },
        ],
      });
      showToast(
        "success",
        `${event.title} is ready for walk-in self check-in.`,
      );
      onLaunched();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLaunching(false);
    }
  };

  const stepLabel: Record<Step, string> = {
    mode: "Workflow",
    award: "Award",
    modeDetail: mode === "pre_register" ? "Registration" : "Check-in",
    audience: "Audience",
    invite: "Invitation",
    preview: "Review & launch",
  };

  return (
    <div
      className={
        embedded
          ? "contents"
          : "fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      }
    >
      <div
        className={
          embedded
            ? // mt-6 restores the gap under the unified rail (the `contents` outer
              // above generates no box, so the parent's space-y-6 margin is dropped).
              "bg-white rounded-lg shadow flex flex-col mt-6"
            : "bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        }
      >
        {/* Modal header — suppressed when embedded (the parent supplies the page title). */}
        {!embedded && (
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Set up event: {event.title}
              </h2>
              <p className="text-sm text-gray-500">
                Creates the award, audience, and invitation in one step.
              </p>
            </div>
            <button
              onClick={close}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
        )}

        {/* Step rail — suppressed when embedded; the parent shows one continuous rail. */}
        {!embedded && (
          <div className="px-6 py-3 border-b border-gray-100 flex flex-wrap gap-2 text-xs">
            {stepOrder.map((s, i) => (
              <span
                key={s}
                className={`px-2 py-1 rounded ${
                  s === step
                    ? "bg-primary-600 text-white"
                    : stepOrder.indexOf(step) > i
                      ? "bg-gray-200 text-gray-700"
                      : "bg-gray-50 text-gray-400"
                }`}
              >
                {i + 1}. {stepLabel[s]}
              </span>
            ))}
          </div>
        )}

        <div
          className={`px-6 py-5 space-y-4 ${
            embedded ? "" : "overflow-y-auto flex-1"
          }`}
        >
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
              {error}
            </div>
          )}

          {/* 1. WORKFLOW MODE (iteration 4) */}
          {step === "mode" && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                How should people get in? This decides whether the event needs a
                registration page at all.
              </p>
              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer">
                <input
                  type="radio"
                  checked={mode === "pre_register"}
                  onChange={() => setMode("pre_register")}
                  className="mt-1"
                />
                <span className="text-sm">
                  <span className="block font-medium text-gray-900">
                    Pre-register — invitees sign up in advance
                  </span>
                  <span className="block text-gray-500">
                    The invitation links to a registration page. Registering
                    issues their ticket, so you know who is coming before the
                    day.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer">
                <input
                  type="radio"
                  checked={mode === "show_up"}
                  onChange={() => setMode("show_up")}
                  className="mt-1"
                />
                <span className="text-sm">
                  <span className="block font-medium text-gray-900">
                    Show up — we invite people, but no sign-up form
                  </span>
                  <span className="block text-gray-500">
                    We email everyone you select an invitation with the date,
                    time, and place — but no registration form. They just show
                    up; members self-register at the venue and get their QR and
                    award there. (You’ll pick who gets the invitation next.)
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer">
                <input
                  type="radio"
                  checked={mode === "walk_in_only"}
                  onChange={() => setMode("walk_in_only")}
                  className="mt-1"
                />
                <span className="text-sm">
                  <span className="block font-medium text-gray-900">
                    Walk-in only — no invitation, anyone attending checks in
                  </span>
                  <span className="block text-gray-500">
                    No invitation is sent to anyone — there’s no audience to
                    pick. Anyone at the event identifies themselves and
                    self-checks-in for their QR ticket and award. Self check-in
                    is always on for this event. Best for gun shows and other
                    open-door events.
                  </span>
                </span>
              </label>
            </div>
          )}

          {/* 2. AWARD */}
          {step === "award" && (
            <div className="space-y-4">
              {/* Top-level choice: ticket only vs ticket + automatic upgrade reward */}
              <div className="space-y-2">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="reward-mode"
                    checked={rewardEnabled}
                    onChange={() => setRewardEnabled(true)}
                    className="mt-1"
                  />
                  <span className="text-sm">
                    <span className="block font-medium text-gray-900">
                      Ticket + automatic upgrade reward
                    </span>
                    <span className="block text-gray-500">
                      Attendees get an admission ticket that upgrades to a
                      reward at check-in (default: a $ voucher you set below).
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="reward-mode"
                    checked={!rewardEnabled}
                    onChange={() => setRewardEnabled(false)}
                    className="mt-1"
                  />
                  <span className="text-sm">
                    <span className="block font-medium text-gray-900">
                      Ticket only — no upgrade reward
                    </span>
                    <span className="block text-gray-500">
                      Attendees get a $0 admission ticket (QR). No reward is
                      issued automatically — staff can upgrade it manually on
                      the event page (+$20).
                    </span>
                  </span>
                </label>
              </div>

              {rewardEnabled && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Award type
                    </label>
                    <select
                      value={awardKind}
                      onChange={(e) =>
                        setAwardKind(e.target.value as EibAwardKind)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="voucher">$ Voucher</option>
                      <option value="drawing">
                        Drawing entry (non-monetary)
                      </option>
                      <option value="gift">Physical gift</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Award name
                      </label>
                      <input
                        value={awardName}
                        onChange={(e) => setAwardName(e.target.value)}
                        placeholder="e.g. $25 Range Credit"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                    {awardKind === "voucher" && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Value ($)
                        </label>
                        <input
                          type="number"
                          value={awardValue}
                          onChange={(e) => setAwardValue(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                    )}
                  </div>

                  <div className="border-t pt-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={guestAwardOn}
                        onChange={(e) => setGuestAwardOn(e.target.checked)}
                        className="mt-1"
                      />
                      <span className="text-sm">
                        <span className="block font-medium text-gray-900">
                          Also award guests
                        </span>
                        <span className="block text-gray-500">
                          Off by default: guests are welcome and get admission,
                          but only members get an award. Turn on to give guests
                          their own (usually different) award — the invitation
                          wording changes to match.
                        </span>
                      </span>
                    </label>
                    {guestAwardOn && (
                      <div className="mt-3 pl-7 grid grid-cols-3 gap-3">
                        <select
                          value={guestAwardKind}
                          onChange={(e) =>
                            setGuestAwardKind(e.target.value as EibAwardKind)
                          }
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="voucher">$ Voucher</option>
                          <option value="drawing">Drawing entry</option>
                          <option value="gift">Physical gift</option>
                        </select>
                        <input
                          value={guestAwardName}
                          onChange={(e) => setGuestAwardName(e.target.value)}
                          placeholder="Guest award name"
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                        {guestAwardKind === "voucher" && (
                          <input
                            type="number"
                            value={guestAwardValue}
                            onChange={(e) => setGuestAwardValue(e.target.value)}
                            placeholder="Value ($)"
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* 3. MODE DETAIL */}
          {step === "modeDetail" && mode === "pre_register" && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Who is allowed to register and claim a ticket?
              </p>
              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer">
                <input
                  type="radio"
                  checked={registrationMode === "invite_gated"}
                  onChange={() => setRegistrationMode("invite_gated")}
                  className="mt-1"
                />
                <span className="text-sm">
                  <span className="block font-medium text-gray-900">
                    Invite-gated (recommended)
                  </span>
                  <span className="block text-gray-500">
                    Only people who received the invitation can register.
                    Recommended whenever the ticket carries an award.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer">
                <input
                  type="radio"
                  checked={registrationMode === "open"}
                  onChange={() => setRegistrationMode("open")}
                  className="mt-1"
                />
                <span className="text-sm">
                  <span className="block font-medium text-gray-900">
                    Open — anyone with the link
                  </span>
                  <span className="block text-gray-500">
                    Anyone who finds the page can register. Set a cap to limit
                    how many people can register online.
                  </span>
                </span>
              </label>
              {registrationMode === "open" && (
                <div className="pl-7">
                  <label className="block text-sm text-gray-600 mb-1">
                    Maximum online registrations (required)
                  </label>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={openCap}
                    onChange={(e) => setOpenCap(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Open registration isn&apos;t limited by an invite list, so a
                    positive cap is required. Once this many tickets exist, the
                    registration page stops accepting sign-ups and tells
                    visitors registration is closed.
                  </p>
                  <p className="mt-1 text-xs text-gray-600">
                    <span className="font-medium">
                      This does not close the door.
                    </span>{" "}
                    Walk-ins who self check-in at the event are never turned
                    away by this cap — they still get a ticket. Their tickets do
                    count toward the number above, though, so a busy door can
                    close online registration earlier than you planned. Set this
                    to the number you want to pre-register, not the size of the
                    venue.
                  </p>
                </div>
              )}
            </div>
          )}

          {step === "modeDetail" &&
            (mode === "show_up" || mode === "walk_in_only") && (
              <div className="space-y-3">
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
                  <p className="font-medium mb-1">
                    {mode === "walk_in_only"
                      ? "Walk-in only — self check-in is on for this event."
                      : "This event will accept walk-up self check-in."}
                  </p>
                  <p>
                    Because there&apos;s no pre-registration, members identify
                    themselves at the venue and get their ticket and award there
                    — each self check-in is recorded as an attendee, so you
                    still get a real headcount. The self check-in page only
                    works on the event&apos;s own day, and only for this event
                    while it is running.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    How many people do you expect?{" "}
                    <span className="font-normal text-gray-400">
                      (optional)
                    </span>
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={expectedCapacity}
                    onChange={(e) => setExpectedCapacity(e.target.value)}
                    placeholder="e.g. 250"
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Optional planning estimate for awards and staffing. Each
                    self check-in is counted as an attendee, so you&apos;ll see
                    real numbers as people arrive. If you set it, it also caps
                    how many tickets self check-in will issue — leave blank for
                    no cap.
                  </p>
                </div>
                {mode === "show_up" && (
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checkinAck}
                      onChange={(e) => setCheckinAck(e.target.checked)}
                      className="mt-1"
                    />
                    <span className="text-sm text-gray-700">
                      I understand public self check-in will be open for this
                      event on the day of the event.
                    </span>
                  </label>
                )}
              </div>
            )}

          {/* 4. AUDIENCE */}
          {step === "audience" && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                {mode === "show_up"
                  ? "Who receives the “just show up” invitation (date, time, and place — no sign-up form)."
                  : "Who to invite to register for this event."}
              </p>
              <div className="flex gap-4 text-sm">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={audienceKind === "existing"}
                    onChange={() => setAudienceKind("existing")}
                  />
                  Existing segment
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={audienceKind === "proximity"}
                    onChange={() => setAudienceKind("proximity")}
                  />
                  People near the event
                </label>
              </div>

              {audienceKind === "existing" ? (
                <>
                  <select
                    value={existingListId}
                    onChange={(e) => setExistingListId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="">Choose a segment…</option>
                    {audienceLists.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                        {l.is_dynamic ? " · rule-based" : ""}
                        {l.contact_count !== null
                          ? ` (${l.contact_count.toLocaleString()})`
                          : l.is_dynamic
                            ? " (sized at launch)"
                            : ""}
                      </option>
                    ))}
                  </select>
                  {existingListId &&
                    audienceLists.find((l) => l.id === existingListId)
                      ?.is_dynamic && (
                      <p className="mt-1 text-xs text-gray-500">
                        Rule-based segment — its members are captured as a
                        snapshot when you launch, and the exact count is
                        confirmed then.
                      </p>
                    )}
                </>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      Who to invite
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Pick any combination — the invitation goes to everyone
                      near the event who matches at least one selected type.
                    </p>
                    <div
                      className="space-y-2"
                      role="group"
                      aria-label="Proximity contact types"
                    >
                      {(
                        [
                          {
                            value: "current",
                            title: "Current member",
                            copy: "People with an active subscription (the authoritative Zuora membership signal).",
                          },
                          {
                            value: "former",
                            title: "Former member",
                            copy: "People who were members but are lapsed or cancelled.",
                          },
                          {
                            value: "prospect",
                            title: "Prospect",
                            copy: "Contacts labeled Prospect who aren't current or former members.",
                          },
                        ] as const
                      ).map((opt) => {
                        const isChecked = contactTypes.includes(opt.value);
                        return (
                          <label
                            key={opt.value}
                            className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer ${
                              isChecked
                                ? "border-primary-500 bg-primary-50"
                                : "border-gray-300 hover:border-gray-400"
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="mt-0.5"
                              value={opt.value}
                              checked={isChecked}
                              onChange={(e) =>
                                setContactTypes((prev) =>
                                  e.target.checked
                                    ? [...prev, opt.value]
                                    : prev.filter((t) => t !== opt.value),
                                )
                              }
                            />
                            <span className="text-sm">
                              <span className="font-medium text-gray-800">
                                {opt.title}
                              </span>
                              <span className="block text-xs text-gray-500">
                                {opt.copy}
                              </span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                    {contactTypes.length === 0 && (
                      <p className="mt-1 text-xs text-amber-700">
                        Select at least one contact type to invite.
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">
                        Radius (miles)
                      </label>
                      <input
                        type="number"
                        value={radiusMiles}
                        onChange={(e) => setRadiusMiles(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">
                        Centered on
                      </label>
                      <select
                        value={anchorType}
                        onChange={(e) =>
                          setAnchorType(
                            e.target.value as "organization" | "zip",
                          )
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="organization">
                          The event&apos;s location
                        </option>
                        <option value="zip">A ZIP code</option>
                      </select>
                    </div>
                  </div>
                  {anchorType === "zip" && (
                    <input
                      value={anchorZip}
                      onChange={(e) => setAnchorZip(e.target.value)}
                      placeholder="5-digit ZIP"
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  )}
                  {anchorType === "organization" &&
                    !eventIsGeocoded &&
                    !eventOrgId && (
                      <p className="text-sm text-amber-700">
                        This event isn't geocoded and has no organization to
                        anchor to — choose a ZIP instead.
                      </p>
                    )}
                </div>
              )}
            </div>
          )}

          {/* 5. INVITATION */}
          {step === "invite" && (
            <div className="space-y-3">
              <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-700">
                {/* The invite step is hidden for walk_in_only, so mode is pre_register/show_up here. */}
                {describeBlueprint(mode as EibWorkflowMode, guestAwardOn)}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subject line
                </label>
                <input
                  value={flightSubject}
                  onChange={(e) => setFlightSubject(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <p className="text-xs text-gray-500">
                The email design is chosen automatically from the workflow and
                the guest-award setting above, so the invitation can never
                promise an award this event will not issue.
              </p>
              {typeCadence ? (
                /* Fixed-cadence event type: the server owns the timing. Show it
                   read-only; the operator picks neither the send time nor the
                   reminders. */
                <div className="pt-2 border-t border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Invitation schedule
                  </label>
                  <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-700 space-y-1">
                    <p>
                      This event type sends on a fixed schedule. The invitation
                      goes out{" "}
                      <span className="font-medium">
                        {cadenceOffsetLabel(typeCadence.inviteOffsetDays)}
                      </span>
                      {typeCadence.reminderOffsets.length > 0 ? (
                        <>
                          , with a reminder{" "}
                          <span className="font-medium">
                            {typeCadence.reminderOffsets
                              .map(cadenceOffsetLabel)
                              .join(", ")}
                          </span>
                          .
                        </>
                      ) : (
                        ", with no reminder."
                      )}
                    </p>
                    <p className="text-xs text-gray-500">
                      All sends are at 8:00&nbsp;AM in the event's local time.
                      The timing is set automatically for this event type and
                      can't be changed here.
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Send the invitation
                    </label>
                    <input
                      type="datetime-local"
                      value={flightSendAt}
                      onChange={(e) => setFlightSendAt(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>

                  {/* Slice 2: invitation reminders */}
                  <div className="pt-2 border-t border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Reminders
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { offset: 2, label: "2 days before" },
                        { offset: 1, label: "1 day before" },
                        { offset: 0, label: "Day of (8am)" },
                      ].map(({ offset, label }) => {
                        const on = reminderOffsets.includes(offset);
                        return (
                          <button
                            key={offset}
                            type="button"
                            aria-pressed={on}
                            onClick={() =>
                              setReminderOffsets((prev) =>
                                prev.includes(offset)
                                  ? prev.filter((o) => o !== offset)
                                  : [...prev, offset],
                              )
                            }
                            className={`px-3 py-1.5 rounded-lg text-sm border ${
                              on
                                ? "bg-blue-600 text-white border-blue-600"
                                : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
                            }`}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Reminders go only to people who received the invitation,
                      at 8:00&nbsp;AM in the event's local time. Choose none to
                      skip reminders.
                    </p>
                  </div>
                </>
              )}
            </div>
          )}

          {/* 6. PREVIEW / LAUNCH */}
          {step === "preview" && !launchResult && (
            <div className="space-y-3">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  {resumeCandidate
                    ? "Here is what will be set up — continuing your earlier attempt"
                    : "Here is what will be set up — nothing has been created yet"}
                </h4>
                <ul className="text-sm text-gray-700 space-y-1.5 list-disc pl-5">
                  {summarizePlan(buildPlan(), audienceCount).map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ul>
              </div>
              <p className="text-xs text-gray-500">
                The audience is frozen to this event when you launch, so later
                edits to the segment cannot change who is invited. The
                invitation is scheduled last — if any step fails, nothing will
                have been emailed.
              </p>
              {resumeCandidate && !resumeOptOut && (
                <div
                  className="p-3 bg-blue-50 border border-blue-200 rounded-lg"
                  data-testid="eib-resume-notice"
                >
                  <p className="text-sm text-blue-900">
                    <span className="font-medium">
                      Picking up where your last attempt stopped.
                    </span>{" "}
                    The attempt you started
                    {resumeCandidate.startedAt
                      ? ` ${new Date(resumeCandidate.startedAt).toLocaleString()}`
                      : ""}{" "}
                    stopped while{" "}
                    {resumeStepPhrase(resumeCandidate.failedAtStep)} and sent
                    nothing. Launch reuses what it already set up
                    {resumeCandidate.audienceCount !== null
                      ? ` (including the frozen audience of ${resumeCandidate.audienceCount.toLocaleString()} contacts)`
                      : ""}{" "}
                    instead of creating another draft campaign.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      // A fresh setup needs its own key; the earlier draft stays unsent and is
                      // retired automatically once a later launch is scheduled.
                      setResumeOptOut(true);
                      setResumeCandidate(null);
                      setIdempotencyKey(crypto.randomUUID());
                    }}
                    className="mt-2 text-sm font-medium text-blue-700 underline hover:text-blue-900"
                  >
                    Start a fresh setup instead
                  </button>
                </div>
              )}
              {resumeOptOut && (
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700">
                  Launch will create a fresh setup. The earlier attempt&apos;s
                  draft stays unsent and is retired automatically once this
                  launch is scheduled.
                </div>
              )}
              {rehydrateStatus === "loading" && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900">
                  Checking for a previous setup for this event…
                </div>
              )}
              {(rehydrateStatus === "error" ||
                rehydrateStatus === "unusable") && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-900 mb-2">
                    {rehydrateStatus === "unusable"
                      ? "This event has a saved setup in a format this version can't read, so it wasn't loaded. Launching now would send with blank defaults — please reload the page to get the latest version before launching."
                      : "Couldn't check whether this event was already set up. Launching now might send with blank defaults instead of the earlier setup — try again before launching."}
                  </p>
                  {rehydrateStatus === "error" && eventId && (
                    <button
                      onClick={() => loadRehydration(eventId)}
                      className="px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700"
                    >
                      Try again
                    </button>
                  )}
                </div>
              )}
              {launching && launchProgress && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900">
                  {launchProgress}
                </div>
              )}
              {confirmPrompt && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-900 mb-2">{confirmPrompt}</p>
                  <input
                    value={typedCount}
                    onChange={(e) => setTypedCount(e.target.value)}
                    placeholder="Type the exact number"
                    className="px-3 py-2 border border-amber-300 rounded-lg text-sm"
                  />
                </div>
              )}
              {additionalWavePrompt && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-900 mb-2">
                    {additionalWavePrompt}
                  </p>
                  <label className="flex items-start gap-2 text-sm text-amber-900 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={allowAdditionalWave}
                      onChange={(e) => {
                        setAllowAdditionalWave(e.target.checked);
                        // A deliberate additional wave is a NEW launch, so it
                        // needs its own key — reusing the old one would just
                        // reconcile to the first launch and send nothing.
                        if (e.target.checked) {
                          setIdempotencyKey(crypto.randomUUID());
                        }
                      }}
                      className="mt-1"
                    />
                    Yes — send an ADDITIONAL invitation wave for this event.
                  </label>
                  <p className="mt-2 text-xs text-amber-800">
                    An additional wave does not skip people already invited. To
                    add people further away, use{" "}
                    <span className="font-medium">
                      Expand invitation radius
                    </span>{" "}
                    on the event&apos;s Campaigns tab.
                  </p>
                </div>
              )}
            </div>
          )}

          {launchResult && (
            <div className="space-y-3">
              <div
                className={`p-4 rounded-lg border ${
                  launchResult.ok
                    ? "bg-green-50 border-green-200"
                    : launchResult.partialSuccess
                      ? "bg-yellow-50 border-yellow-200"
                      : "bg-red-50 border-red-200"
                }`}
              >
                <p
                  className={`font-medium ${
                    launchResult.ok
                      ? "text-green-900"
                      : launchResult.partialSuccess
                        ? "text-yellow-900"
                        : "text-red-900"
                  }`}
                >
                  {launchResult.ok
                    ? mode === "walk_in_only"
                      ? "Event is set up. Walk-in self check-in is on — no invitation."
                      : "Event is set up and the invitation is scheduled."
                    : partialCeiling
                      ? "Current-member invitations ARE scheduled. The former/cancelled win-back audience exceeds the maximum, so that wave was not sent. Current members will NOT be invited again."
                      : launchResult.partialSuccess
                        ? "Current-member invitations ARE scheduled. The former/cancelled win-back wave didn't finish — use “Retry win-back wave” below. Current members will NOT be invited again."
                        : "Launch stopped — no invitation was sent."}
                </p>
                {!launchResult.ok &&
                  !launchResult.partialSuccess &&
                  launchResult.failedAt &&
                  // Only when THIS attempt froze an audience (a launch row with something to adopt).
                  // The dual-wave preflight stops report failedAt "schedule" with no launch at all.
                  launchResult.listId &&
                  EIB_RESUMABLE_STEPS.includes(launchResult.failedAt) && (
                    <p
                      className="mt-1 text-sm text-red-800"
                      data-testid="eib-resume-hint"
                    >
                      What was set up so far is kept. Launching again — with
                      “Back to review” now, or usually by reopening this setup
                      later today — picks up where it stopped instead of
                      starting over.
                    </p>
                  )}
              </div>
              <ul className="text-sm space-y-1">
                {launchResult.steps.map((s) => (
                  <li key={s.key} className="flex items-start gap-2">
                    <span>
                      {s.status === "ok"
                        ? "✅"
                        : s.status === "warning"
                          ? "⚠️"
                          : s.status === "skipped"
                            ? "—"
                            : "❌"}
                    </span>
                    <span
                      className={
                        s.status === "failed"
                          ? "text-red-700"
                          : s.status === "warning"
                            ? "text-amber-700"
                            : "text-gray-700"
                      }
                    >
                      {s.label}
                      {s.detail ? ` — ${s.detail}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <button
            onClick={close}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            {launchResult?.ok || launchResult?.partialSuccess
              ? "Close"
              : embedded
                ? "Skip setup"
                : "Cancel"}
          </button>
          <div className="flex gap-3">
            {!launchResult && step !== "mode" && (
              <button
                onClick={goBack}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                Back
              </button>
            )}
            {!launchResult && !isLastStep && (
              <button
                onClick={goNext}
                className="px-6 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700"
              >
                Next
              </button>
            )}
            {!launchResult && isLastStep && mode === "walk_in_only" && (
              <button
                onClick={doCreateWalkInOnly}
                disabled={launching || rehydrateBlocksLaunch}
                className="px-6 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {launching ? "Creating…" : "Create event"}
              </button>
            )}
            {!launchResult && isLastStep && mode !== "walk_in_only" && (
              <button
                onClick={doLaunch}
                disabled={launching || rehydrateBlocksLaunch}
                className="px-6 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {launching
                  ? "Setting up…"
                  : rehydrateStatus === "loading"
                    ? "Checking…"
                    : "Launch"}
              </button>
            )}
            {launchResult &&
              !launchResult.ok &&
              // A hard-ceiling partial is permanent — no retry offer (Close only).
              (partialCeiling ? null : launchResult.partialSuccess ? (
                // In-session retry reuses the SAME idempotencyKey (state is not
                // reset here), so wave A reconciles idempotently — current members
                // are NOT re-invited — and only the win-back wave completes.
                <button
                  onClick={doLaunch}
                  disabled={launching}
                  className="px-6 py-2 bg-yellow-600 text-white text-sm font-medium rounded-lg hover:bg-yellow-700 disabled:opacity-50"
                >
                  {launching ? "Retrying…" : "Retry win-back wave"}
                </button>
              ) : (
                <button
                  onClick={() => setLaunchResult(null)}
                  className="px-6 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700"
                >
                  Back to review
                </button>
              ))}
            {/* Standalone modal has no built-in CTA step (the create flow does),
                so on a successful launch offer a direct hand-off to the event's
                Call to Action tab instead of dead-ending at "Close". */}
            {!embedded &&
              onAddCta &&
              (launchResult?.ok || launchResult?.partialSuccess) && (
                <button
                  onClick={onAddCta}
                  className="px-6 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700"
                >
                  Add CTA / QR
                </button>
              )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Plain-language phrase for the step an earlier attempt stopped at (resume notice). */
function resumeStepPhrase(step: EibStepKey): string {
  switch (step) {
    case "schedule":
      return "checking the invitation design and scheduling the invitation";
    case "flight":
      return "creating the invitation";
    case "campaign":
      return "creating the campaign";
    case "audience_freeze":
      return "confirming the audience";
    default:
      return "being set up";
  }
}
